// Project draft → survey.v1 document → the exact bytes the outbox keeps and sends.

import { type Project, type RoomDraft, ids, wallLabel } from "../project/model";
import { solveRoom } from "./solve";
import type { Measurement, Room, SurveyDocument } from "./types";

export const APP_NAME = "artisan-tablet";
export const APP_VERSION = "0.1.0";

export interface DeviceInfo {
  model?: string;
  os?: string;
}

export class NotReadyError extends Error {
  constructor(readonly roomId: string, message: string) {
    super(message);
    this.name = "NotReadyError";
  }
}

function need(m: Measurement | null | undefined, room: RoomDraft, what: string): Measurement {
  if (!m) throw new NotReadyError(room.id, `${room.name}: ${what} хэмжээгүй байна`);
  return m;
}

export function buildRoom(room: RoomDraft): Room {
  const solved = solveRoom({
    sketch: room.sketch,
    walls: room.walls.map((w) => w?.um ?? null),
    diagonals: room.diagonals.map((d) => ({ a: d.a, b: d.b, um: d.length?.um ?? null })),
  });
  if (!solved.ok) throw new NotReadyError(room.id, `${room.name}: өрөөний хэлбэр тодорхой болоогүй (${solved.reason})`);
  const n = room.sketch.length;
  const wallSource = (i: number) => room.walls[i]?.source ?? "laser";
  const m = (um: number, source: Measurement["source"]): Measurement => ({ um, source });
  const out: Room = {
    id: room.id,
    name: room.name,
    corners: solved.at.map((at, i) => ({ id: ids.corner(room, i), at })),
    walls: room.walls.map((w, i) => {
      const wall: Room["walls"][number] = {
        id: ids.wall(room, i),
        label: wallLabel(i),
        start: ids.corner(room, i),
        end: ids.corner(room, (i + 1) % n),
        length: need(w, room, `${wallLabel(i)} хана`),
      };
      if (room.heights[i]?.length) wall.heights = room.heights[i];
      return wall;
    }),
    diagonals: room.diagonals.flatMap((d) =>
      d.length ? [{ id: ids.diagonal(room, d.a, d.b), from: ids.corner(room, d.a), to: ids.corner(room, d.b), length: d.length }] : [],
    ),
    ceiling_height: need(room.ceiling, room, "таазны өндөр"),
    openings: room.openings.map((o) => {
      const op: Room["openings"][number] = {
        id: o.id,
        kind: o.kind,
        wall: ids.wall(room, o.wall),
        offset: need(o.offset, room, `${o.id} байрлал`),
        width: need(o.width, room, `${o.id} өргөн`),
        sill: o.kind === "door" ? (o.sill ?? m(0, wallSource(o.wall))) : need(o.sill, room, `${o.id} доод ирмэг`),
        height: need(o.height, room, `${o.id} өндөр`),
      };
      if (o.reveal_depth) op.reveal_depth = o.reveal_depth;
      if (o.swing) op.swing = o.swing;
      return op;
    }),
    utilities: room.utilities.map((u) => ({
      id: u.id,
      kind: u.kind,
      surface: "wall" as const,
      wall: ids.wall(room, u.wall),
      offset: need(u.offset, room, `${u.id} байрлал`),
      height: need(u.height, room, `${u.id} өндөр`),
    })),
    obstructions: room.obstructions.map((o) => ({
      id: o.id,
      kind: o.kind,
      wall: ids.wall(room, o.wall),
      offset: need(o.offset, room, `${o.id} байрлал`),
      width: need(o.width, room, `${o.id} өргөн`),
      elevation: need(o.elevation, room, `${o.id} шалнаас`),
      height: need(o.height, room, `${o.id} өндөр`),
      depth: need(o.depth, room, `${o.id} гүн`),
    })),
    zones: room.zones.map((z) => {
      const src = wallSource(z.wall);
      return {
        id: z.id,
        name: z.name,
        kind: z.kind,
        wall: ids.wall(room, z.wall),
        offset: m(z.offset, src),
        length: m(z.length, src),
        elevation: m(z.elevation, src),
        height: m(z.height, src),
        depth: m(z.depth, src),
        partitions: z.partitions.map((p) => {
          const part: Room["zones"][number]["partitions"][number] = {
            id: p.id,
            offset: m(p.offset, src),
            width: m(p.width, src),
            purpose: p.purpose,
          };
          if (p.label) part.label = p.label;
          return part;
        }),
      };
    }),
  };
  if (room.photo_ids.length) out.photo_ids = room.photo_ids;
  return out;
}

export interface BuildOptions {
  revision_id: string;
  parent_revision_id: string | null;
  captured_at: string;
  device?: DeviceInfo;
}

export function buildSurvey(project: Project, opts: BuildOptions): SurveyDocument {
  const doc: SurveyDocument = {
    format: "artisan.survey",
    version: 1,
    units: "um",
    survey_id: project.id,
    revision_id: opts.revision_id,
    parent_revision_id: opts.parent_revision_id,
    deal_id: project.deal_id,
    captured_at: opts.captured_at,
    device: {
      app: APP_NAME,
      app_version: APP_VERSION,
      ...(opts.device?.model ? { model: opts.device.model.slice(0, 60) } : {}),
      ...(opts.device?.os ? { os: opts.device.os.slice(0, 60) } : {}),
      lidar: false,
    },
    site: { address: project.site.address.trim() },
    rooms: project.rooms.map(buildRoom),
    photos: project.photos.map((p) => {
      const photo: SurveyDocument["photos"][number] = {
        id: p.id,
        sha256: p.sha256,
        content_type: p.content_type,
        size_bytes: p.size_bytes,
      };
      if (p.width_px) photo.width_px = p.width_px;
      if (p.height_px) photo.height_px = p.height_px;
      if (p.taken_at) photo.taken_at = p.taken_at;
      if (p.caption) photo.caption = p.caption.slice(0, 500);
      return photo;
    }),
    notes: project.notes.filter((n) => n.text.trim()),
    approval_render: project.render.approved
      ? { render_result_id: project.render.approved.result_id, approved_at: project.render.approved.approved_at }
      : null,
    sign_off: project.sign_off
      ? {
          name: project.sign_off.name,
          signed_at: project.sign_off.signed_at,
          signature_photo: project.sign_off.photo_id,
          ...(project.sign_off.statement ? { statement: project.sign_off.statement } : {}),
        }
      : null,
  };
  if (project.surveyor_name.trim()) doc.surveyor = { name: project.surveyor_name.trim() };
  if (project.site.customer_name.trim()) {
    doc.site.customer = { name: project.site.customer_name.trim() };
    const phone = project.site.customer_phone.trim();
    if (phone) doc.site.customer.phone = phone;
  }
  const choices = Object.fromEntries(
    Object.entries(project.choices).filter(([, v]) => typeof v === "string" && v.trim()).map(([k, v]) => [k, (v as string).trim().slice(0, 200)]),
  );
  if (Object.keys(choices).length) doc.choices = choices;
  return doc;
}

/** The bytes that are hashed, stored and sent. Never re-serialised after this. */
export function encodeSurvey(doc: SurveyDocument): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(JSON.stringify(doc));
}

export async function sha256Hex(data: Uint8Array | ArrayBuffer | Blob): Promise<string> {
  const buf = data instanceof Blob ? await data.arrayBuffer() : data;
  const digest = await crypto.subtle.digest("SHA-256", buf as ArrayBuffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Hash of what was surveyed, ignoring the ids and time of the revision. */
export async function contentKey(doc: SurveyDocument): Promise<string> {
  const { revision_id: _r, parent_revision_id: _p, captured_at: _c, ...rest } = doc;
  return sha256Hex(new TextEncoder().encode(JSON.stringify(rest)));
}
